import { useStatsigClient } from '@statsig/react-bindings';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { V1UserNotification } from '@/generated/hooks/herald/models';
import { useMarkNotificationsAsRead } from '@/generated/hooks/herald/v1/user_notifications';
import {
  Button,
  cn,
  Flex,
  formatDate,
  Icon,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';

import { ANALYTICS_EVENTS } from '@/shared/analytics/events';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import {
  formatRelativeTime,
  isOlderThanDays,
} from '@/shared/utils/datetime-utils';

interface NotificationCardProps {
  notification: V1UserNotification;
  onClose: () => void;
  onMarkAsRead?: () => void;
}

export function NotificationCard({
  notification,
  onClose,
  onMarkAsRead,
}: NotificationCardProps) {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const { logEvent } = useStatsigClient();
  const orgId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const [isLocallyRead, setIsLocallyRead] = useState(
    notification.is_read ?? false,
  );

  const { mutateAsync: markAsRead } = useMarkNotificationsAsRead();

  const handleClick = async () => {
    if (notification.action?.type === 'redirect' && notification.action.uri) {
      logEvent(ANALYTICS_EVENTS.NOTIFICATION_CLICKED, '', {
        notificationId: notification.id ?? '',
        isRead: String(notification.is_read ?? false),
      });
      navigate(notification.action.uri);
      onClose();

      setIsLocallyRead(true);

      if (!isLocallyRead && notification.id) {
        try {
          await markAsRead({
            organization: orgId,
            workspace: workspaceId,
            data: { notification_ids: [notification.id] },
          });
          onMarkAsRead?.();
        } catch {
          // Silently fail — panel is already closed
        }
      }
    }
  };

  const handleMarkAsRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!notification.id) return;

    setIsLocallyRead(true);

    try {
      await markAsRead({
        organization: orgId,
        workspace: workspaceId,
        data: { notification_ids: [notification.id] },
      });
      onMarkAsRead?.();
    } catch {
      setIsLocallyRead(false);
    }
  };

  const isClickable =
    notification.action?.type === 'redirect' && notification.action.uri;

  return (
    <Flex direction="column" width="full" className="relative">
      <Flex
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        gap={2}
        align="center"
        className={cn(
          'group w-full rounded-md p-3',
          !isLocallyRead && 'bg-accent/20',
          isClickable && 'cursor-pointer hover:bg-accent/50',
        )}
        onClick={handleClick}
        onKeyDown={e => {
          if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {/* Content: rows stacked vertically */}
        <Flex direction="column" gap={0.5} className="min-w-0 flex-1">
          {/* Row 1: dot (if unread) + title + timestamp */}
          <Flex gap={2} align="center" className="w-full">
            {!isLocallyRead && (
              <Flex
                align="center"
                justify="center"
                className="size-[18px] shrink-0"
              >
                <div className="size-2 rounded-full bg-primary" />
              </Flex>
            )}
            <Text
              level="small"
              weight="medium"
              className="min-w-0 flex-1 truncate text-foreground"
            >
              {notification.title}
            </Text>

            {notification.create_time &&
              (isOlderThanDays(notification.create_time, 30) ? (
                <Text level="xSmall" color="muted" className="shrink-0">
                  {formatDate(
                    // eslint-disable-next-line no-restricted-syntax
                    new Date(notification.create_time),
                    'MMM D, h:mm A',
                  )}
                </Text>
              ) : (
                <Tooltip variant="primary">
                  <TooltipTrigger asChild>
                    <Text level="xSmall" color="muted" className="shrink-0">
                      {formatRelativeTime(notification.create_time)}
                    </Text>
                  </TooltipTrigger>

                  <TooltipContent variant="default">
                    {formatDate(
                      // eslint-disable-next-line no-restricted-syntax
                      new Date(notification.create_time),
                      'MMM D, h:mm A',
                    )}
                  </TooltipContent>
                </Tooltip>
              ))}
          </Flex>

          {/* Row 2: body text */}
          {notification.body && (
            <Text
              level="small"
              className={cn(
                'line-clamp-2 break-words text-muted-foreground',
                !isLocallyRead && 'pl-[26px]',
              )}
            >
              {notification.body}
            </Text>
          )}
        </Flex>

        {/* Mark as read button — vertically centered, visible on hover */}
        {!isLocallyRead && (
          <Tooltip variant="primary">
            <TooltipTrigger asChild>
              <Button
                variant="secondary"
                size="icon"
                aria-label={t('notifications.center.card.markAsRead')}
                className="absolute right-6 top-1/2 -translate-y-1/2 !bg-secondary shrink-0 border-none text-muted-foreground opacity-0 transition-opacity focus-visible:ring-0 group-hover:opacity-100"
                onClick={handleMarkAsRead}
              >
                <Icon
                  type="SquareCheckBig"
                  size="sm"
                  className="text-foreground"
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent variant="primary">
              {t('notifications.center.card.markAsRead')}
            </TooltipContent>
          </Tooltip>
        )}
      </Flex>
    </Flex>
  );
}
